import { DivergentRouteContext, DivergentRouteDecision } from './types';
import { Order, SystemConfig } from '../../../../types';

/**
 * ESTRATÉGIA BUMERANGUE
 * 
 * Conceito: Particionar uma rota em duas levas quando há mix de pedidos 
 * prontos e não-prontos, permitindo que o motoboy faça uma "volta" ao hub
 * para buscar o restante.
 * 
 * Lógica de Negócio:
 * 1. Primeira leva: Pedidos prontos saem imediatamente
 * 2. Motoboy entrega primeira leva e RETORNA ao hub
 * 3. Segunda leva: Pedidos que estavam em produção agora estão prontos
 * 4. Motoboy pega segunda leva e entrega
 * 
 * VIABILIDADE (critérios que devem ser atendidos):
 * 
 * C1 - Primeira leva não atrasa:
 *    tempo_ida_primeira + tempo_servico + tempo_volta_hub <= slack_primeira_leva
 * 
 * C2 - Segunda leva não atrasa quando o motoboy voltar:
 *    tempo_volta_hub + tempo_ida_segunda + tempo_servico <= slack_segunda_leva_no_retorno
 * 
 * C3 - Vantagem estratégica real:
 *    - evita estourar SLA da primeira leva no cenário de espera (dispatch hold), OU
 *    - reduz custo de ociosidade do motoboy em nível material
 * 
 * @param context - Dados completos da rota, incluindo tempos calculados
 * @returns Decisão de rota ou null se inviável
 */
export const createBoomerangDecision = (context: DivergentRouteContext): DivergentRouteDecision | null => {
    // Separa pedidos prontos dos não-prontos
    const readyOrders = context.cluster.filter((order) => order.isReady || order.status === 'ready');
    const notReadyOrders = context.cluster.filter((order) => !order.isReady && order.status !== 'ready');
    
    // Se não houver pedidos prontos, não faz sentido bumerangue
    if (readyOrders.length === 0) return null;
    
    // Se não houver pedidos não-prontos, é uma rota normal (não é bumerangue)
    if (notReadyOrders.length === 0) return null;

    // Extrai dados necessários do contexto
    const firstLeg = context.firstLegTiming;
    const secondLeg = context.secondLegTiming;
    const returnTime = context.returnMins;
    // Se não temos os tempos calculados, não podemos avaliar viabilidade
    if (!firstLeg || !secondLeg) {
        console.warn('[Bumerangue] Tempos de sub-rota não calculados, abortando estratégia');
        return null;
    }

    // Margem B configurável: usa valor explícito se existir; fallback mantém comportamento legado.
    const safetyMargin = context.config.fleetConfig.boomerangSafetyMarginMinutes
        ?? Math.max(2, Math.floor((context.config.fleetConfig.slaToleranceMinutes || 10) / 2));

    // C1) A primeira leva precisa caber no SLA dos pedidos já prontos.
    // Importante: aqui analisamos tempo de ENTREGA da primeira leva (sem exigir retorno ao hub).
    const firstLegDeliveryTime = firstLeg.totalMinutes;
    const firstLegReadyMinSlack = Math.min(...readyOrders.map(o => 
        calculateOrderSlack(o, context.config)
    ));
    const c1_firstLegFits = firstLegDeliveryTime <= (firstLegReadyMinSlack - safetyMargin);

    // C2) A segunda leva precisa caber no SLA dos pedidos que ainda estão em produção.
    // Modelagem temporal:
    // - T_hub = tempo até o motoboy voltar ao hub após a 1ª leva
    // - W2    = espera residual no hub se produção ainda não finalizou ao retorno
    // - T2    = entrega da 2ª leva
    // Condição: T_hub + W2 + T2 <= slack_min_nao_prontos - margem
    const timeUntilHubAfterFirstLeg = firstLeg.totalMinutes + returnTime;
    const maxKitchenRemainingNotReady = Math.max(
        ...notReadyOrders.map((order) => getKitchenRemaining(order, context.config.averageKitchenTime || 20))
    );
    const waitForProduction = Math.max(0, maxKitchenRemainingNotReady - timeUntilHubAfterFirstLeg);
    const secondLegCompletionTime = timeUntilHubAfterFirstLeg + waitForProduction + secondLeg.totalMinutes;
    const notReadyMinSlack = Math.min(...notReadyOrders.map(o => 
        calculateOrderSlack(o, context.config)
    ));
    const c2_secondLegFits = secondLegCompletionTime <= (notReadyMinSlack - safetyMargin);

    // C3) Vantagem estratégica (não arbitrária):
    // - Bumerangue é preferível quando o HOLD quebraria SLA da primeira leva,
    //   ou quando a economia de ociosidade do motoboy é material.
    const holdReadyCompletionTime = maxKitchenRemainingNotReady + firstLeg.totalMinutes;
    const holdWouldBreakReadySLA = holdReadyCompletionTime > (firstLegReadyMinSlack - safetyMargin);

    const costPerMinute = getOwnCostPerMinute(context.config);
    const idleIfHold = maxKitchenRemainingNotReady;
    const idleIfBoomerang = waitForProduction;
    const idleSavingsCash = Math.max(0, idleIfHold - idleIfBoomerang) * costPerMinute;
    // Epsilon configurável: ganho mínimo de ociosidade para justificar bumerangue.
    const minMeaningfulIdleSavings = context.config.fleetConfig.boomerangMinIdleSavings ?? 0.5;

    const c3_strategicAdvantage = holdWouldBreakReadySLA || idleSavingsCash >= minMeaningfulIdleSavings;

    // LOG DE DECISÃO (para debugging e auditoria)
    const decisionLog = {
        c1_firstLegFits,
        c2_secondLegFits,
        c3_strategicAdvantage,
        firstLegDeliveryTime,
        firstLegReadyMinSlack,
        timeUntilHubAfterFirstLeg,
        maxKitchenRemainingNotReady,
        waitForProduction,
        secondLegCompletionTime,
        notReadyMinSlack,
        holdReadyCompletionTime,
        holdWouldBreakReadySLA,
        idleIfHold,
        idleIfBoomerang,
        idleSavingsCash,
        safetyMargin,
        readyCount: readyOrders.length,
        notReadyCount: notReadyOrders.length
    };

    // Se algum critério falhar, não fazemos bumerangue (retorna null para cair no else if)
    if (!c1_firstLegFits || !c2_secondLegFits || !c3_strategicAdvantage) {
        return null;
    }

    // ESTRATÉGIA VIÁVEL - Monta a decisão
    return {
        route: {
            type: 'own',
            assigneeId: context.slot.driverId,
            assigneeName: context.slot.driver.name,
            orders: readyOrders,
            cost: context.internalCashCost * (readyOrders.length / context.cluster.length),
            strategyLabel: `${context.strategyPrefix}ENTREGA E VOLTA`,
            isBoomerang: true,
            decisionSummary: `Bumerangue: ${readyOrders.length} pedidos prontos saem agora, ${notReadyOrders.length} aguardam retorno. ` +
                           `1a leva: ${firstLegDeliveryTime.toFixed(1)}min, espera: ${waitForProduction.toFixed(1)}min, 2a leva: ${secondLeg.totalMinutes.toFixed(1)}min. ` +
                           `Margem de segurança: ${safetyMargin}min.`,
            estimatedReturnMinutes: returnTime,
            requiresOvertimeApproval: context.slot.requiresOvertimeApproval
        },
        ownOrders: readyOrders,
        heldOrders: notReadyOrders,
        trace: {
            kind: 'strategy',
            mode: 'hold',
            decisionReason: 'RETENTION_STRATEGY',
            orderIds: context.cluster.map((order) => order.id),
            chosenLabel: 'BOOMERANG',
            rationale: `Bumerangue viável: C1=${c1_firstLegFits}, C2=${c2_secondLegFits}, C3=${c3_strategicAdvantage}. ` +
                      `SLA pronto: ${firstLegDeliveryTime.toFixed(1)}<=${(firstLegReadyMinSlack - safetyMargin).toFixed(1)}. ` +
                      `SLA 2a leva: ${secondLegCompletionTime.toFixed(1)}<=${(notReadyMinSlack - safetyMargin).toFixed(1)}. ` +
                      `Ganho estratégico: holdBreakSLA=${holdWouldBreakReadySLA}, idleSavings=R$${idleSavingsCash.toFixed(2)}.`,
            ownCost: context.internalCashCost,
            thirdCost: context.thirdPartyCost,
            slackMin: context.minSlack,
            driverDelayMin: context.slot.delayMins,
            constraints: ['boomerang', 'fleet_profile', 'sla_tolerance_route', 'timing_viability_check']
        },
        decisionRationale: JSON.stringify(decisionLog)
    };
};

/**
 * Helper: Calcula o slack disponível para um pedido específico
 * Usa a mesma lógica do sistema de safe dispatch
 */
function calculateOrderSlack(order: Order, config: SystemConfig): number {
    const elapsedMins = (Date.now() - (order.createdAt || Date.now())) / 60000;
    const timeLimit = order.timeLimit || 60; // default 60 min
    // Tempo restante de produção (apenas para retorno, NÃO para cálculo de slack)
    // A produção acontece em paralelo ao deslocamento
    const kitchenRemaining = (!order.isReady && order.status !== 'ready')
        ? Math.max(0, (config.averageKitchenTime || 20) - elapsedMins)
        : 0;
    
    const travelTime = (order.estimatedDuration || 10);
    const serviceTime = config.fleetConfig.serviceTimeMinutes || 3;
    const tolerance = config.fleetConfig.slaToleranceMinutes || 10;
    
    // Slack = deadline - elapsed - travel - service + tolerance
    // NÃO inclui kitchenTime (tempo de produção)
    return Math.max(0, timeLimit - elapsedMins - travelTime - serviceTime + tolerance);
}

function getKitchenRemaining(order: Order, avgKitchenTime: number): number {
    if (order.isReady || order.status === 'ready') return 0;
    const elapsedMins = (Date.now() - (order.createdAt || Date.now())) / 60000;
    return Math.max(0, avgKitchenTime - elapsedMins);
}

function getOwnCostPerMinute(config: SystemConfig): number {
    const dailyFee = config.fleetConfig?.dailyFixedCost || 0;
    const shiftHours = config.fleetConfig?.averageShiftHours || 8;
    return dailyFee / (shiftHours * 60);
}
